import React, { useEffect, useMemo, useState } from "react";

/**
 * SurveyAccessPanel (v2)
 * --------------------------------------------------------------
 * Updates per request:
 *  - Radius: optional address input so the radius can be relative to a custom address
 *    (when device geolocation is inaccurate/disabled).
 *  - Order: move "Require access code" to the bottom.
 *  - Exclusivity: radius and region are now mutually exclusive. Enabling one
 *    turns the other off (keeps UX simple). You can later add an "Advanced"
 *    flag to allow combining if desired.
 *
 * PROPS
 *   value?: {
 *     // radius
 *     radiusEnabled: boolean,
 *     radiusFeet: number | "",
 *     radiusOrigin: "device" | "address",
 *     radiusAddress: string,
 *     // region
 *     regionEnabled: boolean,
 *     regionScope: "city" | "county" | "state" | "",
 *     regionName: string,
 *     regionCode?: string | null,
 *     // code
 *     codeEnabled: boolean,
 *     code: string,
 *   }
 *   onChange(nextValue): void
 *
 * SUBMIT SHAPE (example)
 *   const payload = {
 *     ...otherFields,
 *     visibility: {
 *       radius: value.radiusEnabled ? {
 *         feet: Number(value.radiusFeet),
 *         origin: value.radiusOrigin,             // 'device' | 'address'
 *         address: value.radiusOrigin === 'address' ? value.radiusAddress : null,
 *       } : null,
 *       region: value.regionEnabled ? {
 *         scope: value.regionScope, name: value.regionName, code: value.regionCode || null,
 *       } : null,
 *       code: value.codeEnabled ? value.code : null,
 *     }
 *   };
 *
 * BACKEND NOTES
 *   - If radius.origin === 'address', geocode address → (lat,lon) server-side and store it
 *     with the survey. Then filter with ST_DWithin(device_point, address_point, meters).
 *   - Keep radius and region mutually exclusive at query time.
 */

export default function SurveyAccessPanel({ value, onChange }) {
  const [v, setV] = useState(() => ({ ...SurveyAccessPanel.defaults, ...(value || {}) }));

  useEffect(() => {
    onChange?.(v);
  }, [v, onChange]);

  const set = (patch) => setV((prev) => ({ ...prev, ...patch }));

  // ---------------------- Validation ----------------------
  const codeErr = useMemo(() => {
    if (v.mode !== 'code') return "";
    if (!v.code || v.code.length < 4) return "Code must be at least 4 characters";
    if (/\s/.test(v.code)) return "No spaces allowed in code";
    return "";
  }, [v.mode, v.code]);

  const exportCodeErr = useMemo(() => {
    if (!v.exportHtmlProtected) return "";
    if (!v.exportHtmlCode || v.exportHtmlCode.length < 4) return "Export code must be at least 4 characters";
    if (/\s/.test(v.exportHtmlCode)) return "No spaces allowed in export code";
    return "";
  }, [v.exportHtmlProtected, v.exportHtmlCode]);

  const hasAnyError = Boolean(codeErr || exportCodeErr);

  // ---------------------- UI ----------------------
  return (
    <section className="s-panel">
      <h3 className="s-title">Visibility & Access</h3>
      <p className="s-sub">Choose who can access this survey.</p>

      {/* Visibility mode */}
      <div className="s-card">
        <div className="s-choice">
          <label className="s-row-sm">
            <input
              type="radio"
              name="vis-mode"
              value="global"
              checked={v.mode === 'global'}
              onChange={() => set({ mode: 'global' })}
            />
            <span>Public (visible to everyone)</span>
          </label>
          <label className="s-row-sm">
            <input
              type="radio"
              name="vis-mode"
              value="code"
              checked={v.mode === 'code'}
              onChange={() => set({ mode: 'code' })}
            />
            <span>Require access code</span>
          </label>
        </div>
        {v.mode === 'code' && (
          <div className="s-field" style={{ marginTop: 8 }}>
            <label>Access code</label>
            <input
              type="text"
              value={v.code}
              onChange={(e) => set({ code: e.target.value.trim() })}
              placeholder="e.g., JOESCOFFEE24"
              maxLength={32}
            />
            {codeErr && <div className="s-err">{codeErr}</div>}
            <small className="s-hint">People need this code to view and respond.</small>
          </div>
        )}
      </div>

      <div className="s-card">
        <label className="s-row-sm">
          <input
            type="checkbox"
            name="export-html-protected"
            checked={Boolean(v.exportHtmlProtected)}
            onChange={(e) => set({ exportHtmlProtected: e.target.checked })}
          />
          <span>Require a separate code to export insights (HTML)</span>
        </label>
        {v.exportHtmlProtected && (
          <div className="s-field" style={{ marginTop: 8 }}>
            <label>Insights export code</label>
            <input
              type="text"
              value={v.exportHtmlCode}
              onChange={(e) => set({ exportHtmlCode: e.target.value.trim() })}
              placeholder="e.g., ANALYTICS2024"
              maxLength={32}
            />
            {exportCodeErr && <div className="s-err">{exportCodeErr}</div>}
            <small className="s-hint">Share only with collaborators who should export HTML insights.</small>
          </div>
        )}
      </div>

      {/* Expose validity so parent can disable submit */}
      <input type="hidden" name="survey_access_valid" value={String(!hasAnyError)} />
    </section>
  );
}

SurveyAccessPanel.defaults = {
  mode: 'global', // 'global' | 'code'
  code: '',
  exportHtmlProtected: false,
  exportHtmlCode: '',
};

/* ------------------------------------------------------------------------
   Minimal styles (scoped class names). You can move to your CSS files.
   ------------------------------------------------------------------------ */
const styles = `
.s-panel { border: 1px solid #e4e4e7; border-radius: 12px; padding: 16px; background: #fff; }
.s-title { margin: 0 0 4px; font-size: 18px; }
.s-sub { margin: 0 0 12px; color: #555; font-size: 14px; }
.s-card { border: 1px solid #eee; border-radius: 10px; padding: 12px; margin: 10px 0; background: #fafafa; }
.s-row { display: flex; align-items: center; gap: 8px; font-weight: 600; }
.s-row-sm { display:flex; align-items:center; gap:8px; font-weight: 500; }
.s-label { font-size: 14px; }
.s-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 10px; }
.s-field label { display: block; font-size: 13px; color: #333; margin-bottom: 6px; }
.s-field input[type=text], .s-field input[type=number], .s-field select { width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 8px; }
.s-choice { display: grid; gap: 6px; margin-top: 6px; }
.s-err { color: #b42318; background:#fee4e2; border:1px solid #fecdca; padding:6px 8px; border-radius:8px; margin-top:6px; font-size:12px; }
.s-hint { color: #666; font-size: 12px; }
.s-note { font-size: 13px; background:#f6f8fa; border:1px dashed #d0d7de; padding:8px; border-radius:8px; }
.s-summary { margin-top: 14px; padding-top: 8px; border-top: 1px solid #eee; font-size: 14px; }
`;

// Inject styles once per page
if (typeof document !== "undefined" && !document.getElementById("survey-access-panel-styles")) {
  const tag = document.createElement("style");
  tag.id = "survey-access-panel-styles";
  tag.textContent = styles;
  document.head.appendChild(tag);
}
